import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';

const PasscodeInput = ({ length = 6, value, onChange, disabled = false, autoFocus = false, masked = false }) => {
  const [digits, setDigits] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (value) {
      setDigits(value.split('').concat(Array(length).fill('')).slice(0, length));
    }
  }, [value, length]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, val) => {
    if (!/^\d*$/.test(val)) return; // Only allow digits

    const newDigits = [...digits];
    newDigits[index] = val.slice(-1); // Only take last digit
    setDigits(newDigits);

    // Call onChange with complete passcode
    const passcode = newDigits.join('');
    onChange(passcode);

    // Auto-focus next input
    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle arrow keys
    else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    const newDigits = pastedData.split('').concat(Array(length).fill('')).slice(0, length);
    setDigits(newDigits);
    onChange(pastedData);

    // Focus last filled input or first empty
    const nextIndex = Math.min(pastedData.length, length - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  return (
    <div className="flex gap-2 md:gap-3 justify-center">
      {digits.map((digit, index) => (
        <Input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type={masked ? "password" : "text"}
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-12 h-12 md:w-14 md:h-14 text-center text-xl md:text-2xl font-bold bg-white border-2 border-stone-300 rounded-lg focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/20"
        />
      ))}
    </div>
  );
};

export default PasscodeInput;
